/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import ConversationContainer from 'bundles/ai-course-coach/components/Conversation/ConversationContainer';
import { Launcher } from 'bundles/ai-course-coach/components/Launcher';
import type { GlobalState } from 'bundles/ai-course-coach/store/types';
import type { AnyFunction, HomeView, SendMessageFunc } from 'bundles/ai-course-coach/utils/types';
import localStorageEx from 'bundles/common/utils/localStorageEx';

import useFullScreenAnimation from './Conversation/utils/useFullScreenToggleAnimation/useFullScreenAnimation';

const TOOLTIP_LOCAL_STORAGE_KEY = 'coursera_coach_tooltip_dont_show';

type Props = {
  onSendMessage: SendMessageFunc;
  onToggleConversation: AnyFunction;
  onToggleFullScreen: AnyFunction;
  senderPlaceHolder: string;
  profileClientAvatar?: string;
  showCloseButton: boolean;
  showFullScreenButton: boolean;
  autofocus: boolean;
  customLauncher?: AnyFunction;
  onTextInputChange?: (text: string) => void;
  chatId: string;
  handleNewSession?: () => void;
  homeView?: HomeView;
  loading?: boolean;
  courseId?: string;
};

function WidgetLayout({
  onSendMessage,
  onToggleConversation,
  onToggleFullScreen,
  senderPlaceHolder,
  profileClientAvatar,
  showCloseButton,
  showFullScreenButton,
  autofocus,
  customLauncher,
  onTextInputChange,
  chatId,
  handleNewSession,
  homeView,
  loading,
  courseId,
}: Props) {
  const { disableInput, showChat, isFullScreen } = useSelector((state: GlobalState) => ({
    showChat: state.behavior.showChat,
    isFullScreen: state.behavior.isFullScreen,
    disableInput: state.behavior.disabledInput,
  }));

  const messageRef = useRef<HTMLDivElement | null>(null);
  const { fullScreenCSS, exitFullScreenAnimation, startFullScreenAnimation } = useFullScreenAnimation();

  const isMobile = useMediaQuery(breakpoints.down('xs'));

  const handleToggleConversation = () => {
    if (showChat && isFullScreen) {
      // clear fullscreen styles on closing chat
      exitFullScreenAnimation();
    }

    onToggleConversation();
  };

  useEffect(() => {
    if (showChat) {
      messageRef.current = document.getElementById('messages') as HTMLDivElement;
    }
    // removing tooltip from local storage to clean up people who have already seen it
    localStorageEx.removeItem(TOOLTIP_LOCAL_STORAGE_KEY);
    return () => {
      messageRef.current = null;
    };
  }, [showChat]);

  return (
    <div
      css={css`
        height: 100%;
      `}
    >
      {showChat && (
        <div
          css={[
            css`
              z-index: 10;
              position: fixed;
              right: 0;
              bottom: 0;
              height: calc(100vh - 90px);
              width: 400px;
              max-width: 400px;
              margin: 0 20px 20px 0;

              ${breakpoints.down('xs')} {
                width: 100%;
                max-width: 100vw;
                height: 100%;
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                top: 0;
              }
            `,
            fullScreenCSS,
          ]}
        >
          <ConversationContainer
            sendMessage={onSendMessage}
            senderPlaceHolder={senderPlaceHolder}
            profileClientAvatar={profileClientAvatar}
            toggleChat={handleToggleConversation}
            showCloseButton={showCloseButton}
            showFullScreenButton={isMobile ? false : showFullScreenButton} // hide on mobile since chat is always fullscreen when opened
            isFullScreen={isFullScreen}
            disabledInput={disableInput}
            autofocus={autofocus}
            className={showChat ? 'active' : 'hidden'}
            onTextInputChange={onTextInputChange}
            toggleFullScreen={() => {
              if (!isFullScreen) {
                startFullScreenAnimation();
              } else {
                exitFullScreenAnimation();
              }
              onToggleFullScreen();
            }}
            handleNewSession={handleNewSession}
            homeView={homeView}
            loading={loading}
            courseId={courseId}
          />
        </div>
      )}
      {customLauncher ? (
        customLauncher(handleToggleConversation)
      ) : (
        <Launcher toggle={handleToggleConversation} chatId={chatId} />
      )}
    </div>
  );
}

export default WidgetLayout;
